export type AnalyticsSessionProperties = {
  session_id: string;
  session_started_at: string;
  session_last_activity_at: string;
  session_age_seconds: number;
  session_inactivity_timeout_minutes: number;
};

type AnalyticsSessionContext = {
  session_id: string;
  session_started_at: string;
  session_last_activity_at: string;
};

const ANALYTICS_SESSION_STORAGE_KEY = 'konnect_mixpanel_session';
const SESSION_INACTIVITY_TIMEOUT_MS = 30 * 60 * 1000;

const createSessionId = (): string => {
  return `${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 10)}`;
};

const createSessionContext = (now: Date): AnalyticsSessionContext => {
  const timestamp = now.toISOString();

  return {
    session_id: createSessionId(),
    session_started_at: timestamp,
    session_last_activity_at: timestamp,
  };
};

const readSessionContext = (): AnalyticsSessionContext | undefined => {
  try {
    const value = window.sessionStorage.getItem(ANALYTICS_SESSION_STORAGE_KEY);

    if (!value) {
      return undefined;
    }

    const sessionContext = JSON.parse(value);

    if (
      typeof sessionContext.session_id !== 'string' ||
      typeof sessionContext.session_started_at !== 'string' ||
      typeof sessionContext.session_last_activity_at !== 'string'
    ) {
      return undefined;
    }

    return sessionContext;
  } catch {
    return undefined;
  }
};

const writeSessionContext = (sessionContext: AnalyticsSessionContext) => {
  try {
    window.sessionStorage.setItem(ANALYTICS_SESSION_STORAGE_KEY, JSON.stringify(sessionContext));
  } catch {
    // Session metadata is useful for flow mining, but analytics should not break the app.
  }
};

const getSessionAgeSeconds = (sessionStartedAt: string, now: Date): number => {
  const sessionStartedAtMs = new Date(sessionStartedAt).getTime();

  if (Number.isNaN(sessionStartedAtMs)) {
    return 0;
  }

  return Math.max(0, Math.round((now.getTime() - sessionStartedAtMs) / 1000));
};

export const getAnalyticsSessionContext = (): AnalyticsSessionProperties => {
  const now = new Date();
  const currentSessionContext = readSessionContext();
  const lastActivityAtMs = currentSessionContext
    ? new Date(currentSessionContext.session_last_activity_at).getTime()
    : 0;
  const shouldStartNewSession =
    !currentSessionContext ||
    Number.isNaN(lastActivityAtMs) ||
    now.getTime() - lastActivityAtMs > SESSION_INACTIVITY_TIMEOUT_MS;

  const nextSessionContext = shouldStartNewSession
    ? createSessionContext(now)
    : {
        ...currentSessionContext,
        session_last_activity_at: now.toISOString(),
      };

  writeSessionContext(nextSessionContext);

  return {
    session_id: nextSessionContext.session_id,
    session_started_at: nextSessionContext.session_started_at,
    session_last_activity_at: nextSessionContext.session_last_activity_at,
    session_age_seconds: getSessionAgeSeconds(nextSessionContext.session_started_at, now),
    session_inactivity_timeout_minutes: SESSION_INACTIVITY_TIMEOUT_MS / 1000 / 60,
  };
};
